/* eslint-disable no-console */
import PropTypes from 'prop-types';
import { createContext, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  postFilesRequestSDS,
  postDownloadFilesSDS,
  deleteFilesRequestSDS,
  getFilesByFilters,
  getOptionsMonthlyLocationFiles,
  getFilesRequestSDS
} from '../services/serviceRequestAttachment';
import { postDownloadFilesPt, deleteFilesRequestPT } from '../services/workReport';
import useToast from '../hooks/useToast';
import { FILE_DOWNLOAD_TYPES } from '../utils/downloadFiles';

import LoaderContext from './LoaderProvider';

export const FilesSdsPtContext = createContext();

export const FilesSdsPtProvider = ({ children }) => {
  const [dataFilesSDS, setDataFilesSDS] = useState({ actives: [], inactives: [] });
  const [dataFilesPt, setDataFilesPt] = useState({ actives: [], inactives: [] });

  const [isMonthly, setIsMonthly] = useState(false);
  const [dateFilterRange, setDateFilterRange] = useState({
    startDateLimit: '',
    finalDateLimit: ''
  });
  const [infoCompleteSelect, setInfoCompleteSelect] = useState([]);
  const [infoCompleteFilesDeletes, setInfoCompleteSelectDelete] = useState([]);
  const { setNewToast, closeToast } = useToast();
  const { setLoader } = useContext(LoaderContext);
  const { id: idSDS } = useParams();

  const toastOptions = {
    position: 'top-right',
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    progress: undefined
  };

  const showToast = (type, message, opt = null) => {
    setNewToast({
      type,
      message,
      options: opt
    });
  };

  const getDataFilesSDS = async (id) => {
    try {
      setLoader(true);
      const data = await getFilesRequestSDS(id, isMonthly);
      const dataFilesSDSActives = data?.activos || [];
      const dataFilesSDSInactives = data?.inactivos || [];

      setDataFilesSDS({ actives: dataFilesSDSActives, inactives: dataFilesSDSInactives });
    } catch (err) {
      showToast('ERROR', err.message);
    } finally {
      setLoader(false);
    }
  };

  const getDataFilesPt = async (id, details, sortBy = []) => {
    try {
      setLoader(true);
      let orderColumn;

      if (sortBy.length) {
        orderColumn = {
          nombre: sortBy[0]?.id,
          direccion: sortBy[0]?.desc ? 'desc' : 'asc'
        };
      }

      const data = await getFilesByFilters(id, {
        ...details,
        ordenColumna: orderColumn
      });
      const dataFilesPtActives = data?.activos || [];
      const dataFilesPtInactives = data?.inactivos || [];

      setDateFilterRange({
        startDateLimit: data?.fechaDesde,
        finalDateLimit: data.fechaHasta
      });
      setDataFilesPt({ actives: dataFilesPtActives, inactives: dataFilesPtInactives });
    } catch (err) {
      showToast('ERROR', err.message);
    } finally {
      setLoader(false);
    }
  };

  const getSelectOptionsMonthlyLocationFiles = async (id, query) => {
    try {
      return await getOptionsMonthlyLocationFiles(id, query);
    } catch (err) {
      return showToast('ERROR', err.message);
    }
  };

  const setFieldValue = async (files, id) => {
    showToast('INFO', 'Adjuntando archivos, por favor espere.', toastOptions);

    const formData = new FormData();

    files?.map((file) => formData.append('archivosAdjuntos', file));
    try {
      const responseAdjuntos = await postFilesRequestSDS({
        id,
        archivosAdjuntos: formData,
        isMonthly
      });

      if (responseAdjuntos.success) {
        getDataFilesSDS(id);
        closeToast();
        showToast('SUCCESS', 'Archivo subido con éxito.');
      } else {
        closeToast();
        showToast(
          'ERROR',
          responseAdjuntos.errorMessage || 'No se han podido subir los archivos adjuntos.'
        );
      }
    } catch (e) {
      closeToast();
      showToast('ERROR', e.message);
    }
  };

  const getOneFileName = (select, downloadType, isDelete) => {
    if (select.length === 1) {
      return isDelete ? infoCompleteFilesDeletes[0].nombre : infoCompleteSelect[0].nombre;
    }
    if (downloadType === FILE_DOWNLOAD_TYPES.COMBINE_PDF) {
      return 'document.pdf';
    }

    return '';
  };

  const handleIndividualDownload = (select, id, downloadType) =>
    Promise.all(
      select.map((file, index) =>
        postDownloadFilesSDS(infoCompleteSelect[index].nombre, id, [file], isMonthly, downloadType)
      )
    );

  const handleDownloadResponse = (res, oneFile) => {
    if (res.success || (res.length && res.every((r) => r.success))) {
      closeToast();
      showToast(
        'SUCCESS',
        oneFile ? `Descargando ${oneFile}` : 'Descargando archivos.',
        toastOptions
      );
    } else {
      closeToast();
      showToast('ERROR', 'No se pudo completar con la descarga.', toastOptions);
    }
  };

  const downLoadFilesSDS = async (id, select, downloadType, isDelete) => {
    showToast('INFO', 'Descargando, por favor espere.', toastOptions);
    try {
      const oneFile = getOneFileName(select, downloadType, isDelete);
      const isIndividualDownload =
        downloadType === FILE_DOWNLOAD_TYPES.INDIVIDUAL ||
        downloadType === FILE_DOWNLOAD_TYPES.BROWSER;

      const res =
        isIndividualDownload && !oneFile
          ? await handleIndividualDownload(select, id, downloadType)
          : await postDownloadFilesSDS(oneFile, id, select, isMonthly, downloadType);

      handleDownloadResponse(res, oneFile);
    } catch (e) {
      closeToast();
      showToast('ERROR', e.message, toastOptions);
    }
  };

  const getFiles = (select, downloadDeletedFiles) =>
    downloadDeletedFiles
      ? infoCompleteFilesDeletes.filter((file) => select.includes(file.id))
      : infoCompleteSelect.filter((file) => select.includes(file.id));

  const getWorkPartsId = (files) =>
    files.reduce((acc, file) => {
      if (!acc.includes(file.idParteDiario)) {
        acc.push(file.idParteDiario);
      }

      return acc;
    }, []);

  const handleIndividualDownloadPT = (files, downloadType) =>
    Promise.all(
      files.map((file, index) =>
        postDownloadFilesPt(
          infoCompleteSelect[index].nombre,
          [file.idParteDiario],
          [file.id],
          downloadType
        )
      )
    );

  const downLoadFilesPT = async (select, downloadType, downloadDeletedFiles = false) => {
    showToast('INFO', 'Descargando, por favor espere', toastOptions);
    try {
      const oneFile = getOneFileName(select, downloadType, downloadDeletedFiles);
      const isIndividualDownload =
        downloadType === FILE_DOWNLOAD_TYPES.INDIVIDUAL ||
        downloadType === FILE_DOWNLOAD_TYPES.BROWSER;

      const files = getFiles(select, downloadDeletedFiles);
      const workPartsId = getWorkPartsId(files);

      const res =
        isIndividualDownload && !oneFile
          ? await handleIndividualDownloadPT(files, downloadType)
          : await postDownloadFilesPt(oneFile, workPartsId, select, downloadType);

      handleDownloadResponse(res, oneFile);
    } catch (e) {
      closeToast();
      showToast('ERROR', e.message, toastOptions);
    }
  };

  const deleteFilesSDS = async (id, select) => {
    showToast('INFO', 'Eliminando, por favor espere.', toastOptions);

    try {
      const response = await deleteFilesRequestSDS(id, select, isMonthly);
      const oneFile =
        select.length === 1 ? 'Archivo eliminado con éxito' : 'Archivos eliminados con éxito.';

      if (response.success) {
        closeToast();
        showToast('SUCCESS', oneFile);
        getDataFilesSDS(id);
      } else {
        closeToast();
        showToast('ERROR', 'El archivo(s) no se pudo eliminar');
      }
    } catch (e) {
      closeToast();
      showToast('ERROR', e.message);
    }
  };

  const deleteFilesPT = async () => {
    showToast('INFO', 'Eliminando, por favor espere.', toastOptions);

    try {
      const responses = await Promise.all(
        infoCompleteSelect.map((fileInfo) =>
          deleteFilesRequestPT(fileInfo.idParteDiario, [fileInfo.id])
        )
      );
      const oneFile =
        responses.length === 1 ? 'Archivo eliminado con éxito' : 'Archivos eliminados con éxito.';

      if (responses.every((res) => res.success)) {
        closeToast();
        showToast('SUCCESS', oneFile);
        getDataFilesPt(idSDS);
      } else {
        closeToast();
        showToast('ERROR', 'El archivo(s) no se pudo eliminar');
      }
    } catch (e) {
      closeToast();
      showToast('ERROR', e.message);
    }
  };

  return (
    <FilesSdsPtContext.Provider
      value={{
        dataFilesSDS: dataFilesSDS.actives,
        dataFilesPt: dataFilesPt.actives,
        setFieldValue,
        downLoadFilesSDS,
        deleteFilesSDS,
        deleteFilesPT,
        dataInactivesSDS: dataFilesSDS.inactives,
        dataInactivesPt: dataFilesPt.inactives,
        getDataFilesSDS,
        getDataFilesPt,
        setInfoCompleteSelect,
        setIsMonthly,
        infoCompleteSelect,
        infoCompleteFilesDeletes,
        setInfoCompleteSelectDelete,
        downLoadFilesPT,
        getSelectOptionsMonthlyLocationFiles,
        dateFilterRange,
        setDateFilterRange
      }}
    >
      {children}
    </FilesSdsPtContext.Provider>
  );
};

FilesSdsPtProvider.propTypes = {
  children: PropTypes.node.isRequired
};
